import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ArrowRight, BadgeCheck, Building2, Users } from 'lucide-react';

import { Card } from '@/components/ui/card';
import { QueryGate } from '@/components/ui/query-gate';
import { useTranslations } from '@/i18n/use-locale';
import { localDateKey } from '@/lib/detail';
import { formatCount } from '@/lib/format';
import { useAnalyticsSummaryQuery, useReportQuery } from '@/lib/queries';
import { BreakdownCard } from './breakdown-card';
import { isReportPeriod, periodRange } from './report-period';
import { useBreakdowns } from './use-breakdowns';

/**
 * The stats view: the headline numbers on one phone-width page, for an admin to show a
 * club on a visit (Marzouki-org/Marzouki-Admin#100).
 *
 * Outside the console shell on purpose — no sidebar, no header, no queue badges — so the
 * screen held up to a visitor shows the platform's numbers and nothing of the console
 * behind them. Aggregates only: totals, the window's new talents and organizations, and
 * the two breakdowns. Revenue and subscriptions stay on Reports; they are the business's
 * figures, not the platform's.
 *
 * It still needs a session: the API has no anonymous read of these numbers yet. The
 * signed, expiring link the issue asks for — a page that opens on a club's own phone with
 * no session — needs an endpoint to mint the link and one to serve it, both on the
 * Backend; this view is what that link will render, built from the same pieces.
 *
 * Like Reports it asks for no permission of its own: the nav leaves Reports readable by
 * the whole team, and the API checks `reports.read` on each call regardless.
 */
export function StatsRoute() {
  const t = useTranslations('admin.reports');
  const [params] = useSearchParams();
  const requested = params.get('period');
  const period = isReportPeriod(requested) ? requested : 'month';
  const range = useMemo(() => periodRange(period), [period]);

  const summaryQuery = useAnalyticsSummaryQuery();
  const reportQuery = useReportQuery(range.from, range.to);
  const { byCategory, byKind } = useBreakdowns(summaryQuery.data);

  return (
    <main className="mx-auto min-h-screen max-w-xl space-y-5 bg-conc-50 px-4 py-6">
      <Link
        to="/reports"
        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-conc-500 hover:text-navy"
      >
        <ArrowRight aria-hidden className="h-4 w-4 ltr:rotate-180" />
        {t('stats.back')}
      </Link>

      <header>
        <h1 className="text-2xl font-bold text-navy">{t('stats.heading')}</h1>
        <p className="text-[13px] text-conc-500">{t('stats.sub')}</p>
      </header>

      <QueryGate query={summaryQuery}>
        {(summary) => (
          <>
            <div className="grid grid-cols-3 gap-3">
              <Headline icon={Users} label={t('stats.talents')} value={summary.talents.total} />
              <Headline
                icon={BadgeCheck}
                label={t('stats.verifiedTalents')}
                value={summary.talents.verified}
              />
              <Headline
                icon={Building2}
                label={t('stats.organizations')}
                value={summary.organizations.total}
              />
            </div>

            {reportQuery.data && (
              <Card className="p-5">
                <h2 className="mb-3 text-[15px] font-bold text-navy">
                  {t('stats.window', { period: t(`period.${period}`) })}
                </h2>
                <dl className="grid grid-cols-2 gap-3">
                  <div>
                    <dt className="text-[12.5px] text-conc-500">{t('newTalents')}</dt>
                    <dd className="num text-xl font-bold text-navy">
                      {formatCount(reportQuery.data.newTalents)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[12.5px] text-conc-500">{t('newOrganizations')}</dt>
                    <dd className="num text-xl font-bold text-navy">
                      {formatCount(reportQuery.data.newOrganizations)}
                    </dd>
                  </div>
                </dl>
              </Card>
            )}

            {byCategory.length > 0 && (
              <BreakdownCard
                heading={t('byCategory.heading')}
                hint={t('byCategory.hint')}
                rows={byCategory}
              />
            )}
            {byKind.length > 0 && (
              <BreakdownCard heading={t('byKind.heading')} hint={t('byKind.hint')} rows={byKind} />
            )}

            <footer className="space-y-1 text-center text-[11.5px] text-conc-400">
              <p className="num">{t('stats.asOf', { date: localDateKey(new Date()) })}</p>
              <p>{t('stats.aggregatesOnly')}</p>
            </footer>
          </>
        )}
      </QueryGate>
    </main>
  );
}

function Headline({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Users;
  label: string;
  value: number;
}) {
  return (
    <Card className="p-4 text-center">
      <Icon aria-hidden className="mx-auto h-5 w-5 text-pitch" />
      <p className="num mt-2 text-xl font-bold text-navy">{formatCount(value)}</p>
      <p className="text-[11.5px] font-semibold text-conc-500">{label}</p>
    </Card>
  );
}
